/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { FullProduct } from '@msdyn365-commerce/commerce-entities';
import { CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    ICreateActionContext } from '@msdyn365-commerce/core';
import { convertToString, IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';
import { getProductListLinesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductListsDataActions.g';

import { FullProductInput, getFullProducts, getProductDetailsCriteriaFromActionInput, QueryResultSettingsProxy } from '..';
import { ArrayExtensions } from '../utilities';
import { IOrderTemplateLinesState, OrderTemplateLine } from '../utilities/order-templates';

/**
 *  Input class for the GetOrderTemplates data action.
 */
export class GetPaginatedOrderTemplateLinesInput implements IActionInput {
    public orderTemplateId: string;

    public queryResultSettings: IQueryResultSettings;

    public searchText: string;

    constructor(orderTemplateId: string, queryResultSettings: IQueryResultSettings, searchText?: string) {
        this.orderTemplateId = orderTemplateId;
        this.queryResultSettings = queryResultSettings;
        this.searchText = searchText || '';
    }

    public getCacheKey = () => `GetPaginatedOrderTemplateLines|${this.orderTemplateId}|${convertToString(this.queryResultSettings)}|${this.searchText}`;

    public getCacheObjectType = () => 'GetPaginatedOrderTemplateLines';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * CreateInput method for the GetOrderTemplate method.
 * @param inputData The input data passed to the createInput method.
 * @param orderTemplateId
 * @param searchText
 */
export const createGetPaginatedOrderTemplateLinesInput = (
    inputData: ICreateActionContext, orderTemplateId: string, searchText?: string): GetPaginatedOrderTemplateLinesInput => {

    const queryResultSettings: IQueryResultSettings = QueryResultSettingsProxy.fromInputData(inputData).QueryResultSettings;
    queryResultSettings.count = true;
    return new GetPaginatedOrderTemplateLinesInput(orderTemplateId, queryResultSettings, searchText);
};

/**
 * The action method for the GetPaginatedOrderTemplateLines data action.
 * @param input The action input.
 * @param context The action context.
 */
export async function getPaginatedOrderTemplateLinesAction(input: GetPaginatedOrderTemplateLinesInput, context: IActionContext): Promise<IOrderTemplateLinesState> {
    if (!context.requestContext.user.isAuthenticated) {
        throw new Error('Unable to get full order templates. User is not authenticated.');
    }

    const orderTemplateLinesState: IOrderTemplateLinesState = {
        lines: [],
        totalCount: 0
    };
    const promise = getProductListLinesAsync({
        callerContext: context, queryResultSettings: input.queryResultSettings
    }, input.orderTemplateId, input.searchText); const productListLines = await promise;
    const productInputs: FullProductInput[] = [];
    if (ArrayExtensions.hasElements(productListLines)) {
        orderTemplateLinesState.totalCount = promise.metadata.count || productListLines.length;
        productListLines.forEach((listLine) => {
            if (listLine.ProductId) {
                orderTemplateLinesState.lines.push(new OrderTemplateLine(listLine));
                productInputs.push(new FullProductInput(listLine.ProductId,
                    context.requestContext.apiSettings, getProductDetailsCriteriaFromActionInput(context)));
            }
        });
        if (ArrayExtensions.hasElements(productInputs)) {
            const fullProductsInfo = await getFullProducts(productInputs, context)
                .then((products: FullProduct[]) => {
                    if (products) {
                        return products;
                    }
                    return <FullProduct[]>[];

                })
                .catch(error => {
                    context.trace(error);
                    context.telemetry.error(error.message);
                    context.telemetry.error('[getPaginatedOrderTemplateLinesAction] Unable to retrieve product info.');
                });
            if (fullProductsInfo) {
                orderTemplateLinesState.lines.forEach(line => {
                    const fullProduct = fullProductsInfo.find(product => product.ProductDetails.RecordId === line.productListLine.ProductId);
                    if (!fullProduct) {
                        context.telemetry.error('[getPaginatedOrderTemplateLinesAction] Unable to find product info for an order template line.');
                        return;
                    }
                    line.fullProduct = fullProduct;
                });
            }
            return orderTemplateLinesState;
        }
        context.trace(orderTemplateLinesState.lines.length.toString());
        return orderTemplateLinesState;
    }
    return orderTemplateLinesState;
}

/**
 * The GetPaginatedOrderTemplateLines Data Action
 * Returns order templates.
 */
export const getPaginatedOrderTemplateLinesActionDataAction = createObservableDataAction<IOrderTemplateLinesState>({
    id: '@msdyn365-commerce-modules/retail-actions/order-templates/get-paginated-order-template-lines',
    action: getPaginatedOrderTemplateLinesAction as IAction<IOrderTemplateLinesState>,
    input: createGetPaginatedOrderTemplateLinesInput
});

export default getPaginatedOrderTemplateLinesActionDataAction;
